import { concat, defer, Observable, of } from 'rxjs';
import { ofType } from 'redux-observable';
import { catchError, mergeMap, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { EpicDeps } from '@wearejh/m2-pwa-engine/lib/types';
import { ApolloClient } from 'apollo-client';

import { Actions, CartMsg, TypeMap } from '../cart.actions';
import { cartVariantAndId } from '../utils/stateObservables';
import { addProductsToCart, addProductsToCartVariables } from '../queries/__generated__/addProductsToCart';
import addProductsToCartQuery from '../queries/addProductsToCart.graphql';
import { refreshCartAndRetry } from '../utils/refreshCartAndRetry';
import { fetchMineCartId } from '../utils/fetchMineCartId';
import { CartItemInput } from '../queries/__generated__/types';

type InputAction = TypeMap['Cart.Add'];

export function cartAdd(actions$: Observable<any>, state: Observable<any>, deps: EpicDeps) {
    return actions$.pipe(
        ofType<Actions, InputAction>('Cart.Add'),
        withLatestFrom(cartVariantAndId(state)),
        switchMap(([action, [, cartId]]) => {
            if (!cartId) {
                console.error('Could not add since cartId was missing');
                return refreshCartAndRetry(
                    actions$,
                    action,
                    CartMsg('Cart.AddError', 'could not create cart prior to adding'),
                );
            }

            return addProductsMutation(deps, String(cartId), action.payload.items).pipe(
                mergeMap(() => {
                    return concat(of(CartMsg('Cart.AddSuccess'), CartMsg('Cart.Refresh', { force: true })));
                }),
                catchError((e) => {
                    /**
                     * When adding to a basket, we can get an error if the cart is not active.
                     * Example: We have two tabs open.
                     * One tab has an active cart, the other tab has the cart left idle.
                     * In the first tab, the user adds products to the cart and checks out successuly making the cart id invalid.
                     * In the second tab, the user tries to add products to the cart, but the cart ID is no longer active.
                     * Triggering this error:
                     */
                    const isNotActive = e.message.includes("The cart isn't active");

                    if (isNotActive) {
                        /**
                         * In this case, we should:
                         * - fetch the new user cart ID
                         * - fetch the cart using that cart id.
                         * - ON cart fetch success, add the products to the new cart.
                         * - On cart add success, refresh the cart.
                         */
                        return fetchMineCartId(deps).pipe(
                            mergeMap((newCartId: string) =>
                                concat(
                                    of(CartMsg('Cart.Fetch', { cartId: newCartId })),
                                    actions$.pipe(
                                        ofType('Cart.FetchSuccess'),
                                        take(1), // Only listen to the first success
                                        mergeMap(() =>
                                            addProductsMutation(deps, newCartId, action.payload.items).pipe(
                                                mergeMap(() =>
                                                    concat(
                                                        of(CartMsg('Cart.AddSuccess')),
                                                        of(CartMsg('Cart.Refresh', { force: true })),
                                                    ),
                                                ),
                                                catchError((err) => of(CartMsg('Cart.AddError', err.message))),
                                            ),
                                        ),
                                    ),
                                ),
                            ),
                        );
                    }

                    return of(CartMsg('Cart.AddError', e.message));
                }),
            );
        }),
    );
}

export function addProductsMutation(deps: EpicDeps, cartId: string | null, items: CartItemInput[]) {
    return defer(() =>
        (deps.client as ApolloClient<any>).mutate<addProductsToCart, addProductsToCartVariables>({
            mutation: addProductsToCartQuery,
            operationName: 'addProducts',
            variables: {
                cart_id: cartId,
                cart_items: items,
            },
            fetchPolicy: 'no-cache',
        } as any),
    );
}
